import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Stack } from "react-bootstrap";
import { postDwellingOccupant, patchDwellingOccupant } from "../../services/httpClient";
import {
  HousingCandidate,
  CandidateRow,
  buildCaseCandidateRows,
  pluralizeCount,
  pluralizePeople,
  propertyAddressLine,
} from "../../helpers/housingAssignment";
import CandidateSearchPanel from "./CandidateSearchPanel";
import HousingStatus from "../ui/HousingStatus";
import InlineConfirm from "../ui/InlineConfirm";

interface DwellingAssignmentRowState {
  dwelling: any;
  possibleOccupants: any[];
  rooms: any[];
  facilities: any[];
  beds: any[];
  roomCount: number;
  bedCount: number;
  housingCandidates: HousingCandidate[];
  housingByCaseId: Map<number, HousingCandidate>;
  dwellings: any[];
  onAssigned: any;
  onPickerOpenChange: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const DwellingAssignmentRow = ({
  dwelling,
  possibleOccupants,
  rooms,
  facilities,
  beds,
  roomCount,
  bedCount,
  housingCandidates,
  housingByCaseId,
  dwellings,
  onAssigned,
  onPickerOpenChange,
  setErrMessage,
  setShowToast,
  setToastText,
  setToastBody,
}: DwellingAssignmentRowState) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [confirmEnd, setConfirmEnd] = useState(false);

  const occupantCase = dwelling.occupant_case_id ? housingByCaseId.get(dwelling.occupant_case_id) : undefined;
  const occupied = !!occupantCase;

  // a whole-case assignment names no one, so list who that actually is
  const occupantPeople = occupied
    ? possibleOccupants.filter((p: any) => p.case_id === dwelling.occupant_case_id && p.is_residential === 1)
    : [];

  const addressLine = propertyAddressLine(dwelling);

  const onError = (err: any) => {
    setErrMessage(err?.response?.data?.msg);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  // The parent counts open pickers to lock its filters, so an unmount while one is open -
  // collapsing the facility, say - would leave that count stranded above zero forever.
  const pickerOpenRef = useRef(false);
  useEffect(() => {
    pickerOpenRef.current = pickerOpen;
  }, [pickerOpen]);
  useEffect(
    () => () => {
      if (pickerOpenRef.current) onPickerOpenChange(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onClickAssign = () => {
    setErrMessage("");
    setSearch("");
    // not inside the updater: it must stay pure, and React may run it more than once
    const next = !pickerOpen;
    setPickerOpen(next);
    onPickerOpenChange(next);
  };

  const onPickCase = (row: CandidateRow) => {
    const candidate: HousingCandidate = row.candidate;
    setErrMessage("");
    postDwellingOccupant({ property_id: dwelling.id }, candidate.case_id as any)
      .then(() => {
        setShowToast(true);
        setToastText("Dwelling Assigned");
        setToastBody(`${candidate.name} — ${dwelling.name}`);
        setPickerOpen(false);
        onPickerOpenChange(false);
        onAssigned();
      })
      .catch(onError);
  };

  const onConfirmEnd = () => {
    setErrMessage("");
    patchDwellingOccupant(dwelling.occupant_case_id, dwelling.dwelling_occupant_id)
      .then(() => {
        setShowToast(true);
        setToastText("Dwelling Assignment Ended");
        setToastBody(dwelling.name);
        setConfirmEnd(false);
        onAssigned();
      })
      .catch(onError);
  };

  return (
    <Card className="bg-light mb-0">
      <Card.Body className="p-3">
        <Stack direction="horizontal" gap={3} className="flex-wrap">
          <div className="flex-grow-1 order-0">
            <div className="small fw-semibold">{dwelling.name}</div>
            {addressLine && <div className="text-muted small">{addressLine}</div>}
            <div className="small">
              {pluralizeCount(roomCount, "room")} · {pluralizeCount(bedCount, "bed")}
            </div>
          </div>
          {!confirmEnd && (
            <HousingStatus
              dotClass={occupied ? "role-dot role-dot-orange" : "role-dot role-dot-green"}
              label={occupied ? `Occupied · ${occupantCase!.name} · ${pluralizePeople(occupantCase!.size)}` : "Vacant"}
              people={occupantPeople}
            />
          )}
          <Stack direction="horizontal" gap={2} className="flex-wrap order-1 order-xl-3">
            {!occupied && (
              <Button size="sm" className={pickerOpen ? "app-primary-color button" : "app-secondary-color button"} onClick={onClickAssign}>
                {pickerOpen ? "Cancel" : "Assign Case"}
              </Button>
            )}
            {occupied && !confirmEnd && (
              <Button size="sm" className="app-danger-color button" onClick={() => setConfirmEnd(true)}>
                Vacate Case
              </Button>
            )}
            {occupied && confirmEnd && (
              <InlineConfirm
                question={`End ${occupantCase!.name}'s stay here?`}
                onConfirm={onConfirmEnd}
                onCancel={() => setConfirmEnd(false)}
              />
            )}
          </Stack>
        </Stack>

        {pickerOpen && (
          <CandidateSearchPanel
            placeholder="Search cases by name"
            search={search}
            onSearchChange={setSearch}
            rows={buildCaseCandidateRows(housingCandidates, {
              targetKind: "dwelling",
              targetHousingId: dwelling.id,
              capacity: bedCount,
              search,
              dwellings,
              rooms,
              facilities,
              beds,
            })}
            onPick={onPickCase}
            emptyText="No cases match this search."
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default DwellingAssignmentRow;
